import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReduxActions from 'reduxActions';
import { Components, Utils } from 'versafleet-core';
import moment from 'moment';

const { Helper } = Utils;

const timeTypeOptions = [
  {
    value: -1,
    text: (
      <div>
        Custom
        <div className="vf-job__custom-time__time-window__subtext">
          _______ to _______
        </div>
      </div>
    ),
  },
];

class JobCustomDateTime extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      form: this._getDefaultForm(props.value || {}),
      isLoadingTimeWindows: false,
    };
  }

  componentDidMount() {
    this.props.dispatch(ReduxActions.account.fetchTimeWindows());
    this.props.dispatch(ReduxActions.account.fetchTimeWindowsAvailability(this.state.form.date));
  }

  _getDefaultForm = (data) => {
    const form = {};
    form.time_window_id = data.time_window_id;
    form.date = ((data.time_from || data.time_to)
      && moment(data.time_from || data.time_to).format('YYYY-MM-DD'))
      || moment().format('YYYY-MM-DD');
    if (['custom', 'am', 'pm', 'all_day'].includes(data.time_type)) {
      form.time_type = 'custom';
      const from = data.time_from && new Date(data.time_from);
      form.time_from = !Helper.isNullOrUndefined(from)
        ? (from.getHours() * 60) + from.getMinutes() : null;
      const to = data.time_to && new Date(data.time_to);
      form.time_to = !Helper.isNullOrUndefined(to) ? (to.getHours() * 60) + to.getMinutes() : null;
    } else {
      form.time_type = data.time_type;
      form.time_from = null;
      form.time_to = null;
    }
    return form;
  }

  _getTimeWindowOptions() {
    const { shouldFilterTimeWindows, timeWindows, timeWindowsAvailability } = this.props;
    const { form } = this.state;

    const additionalTimeOptions = this.props.allowCustomTimeWindows
      ? timeTypeOptions
      : [];

    const timeWindowOptions = timeWindows.filter((timeWindow) => {
      if (!shouldFilterTimeWindows) {
        return true;
      }
      if (timeWindow.should_consider_capacity) {
        const twa = timeWindowsAvailability[form.date]?.find(
          tw => tw.time_window_id === timeWindow.id,
        );

        if (twa && twa.availability <= 0) {
          return false;
        }
      }

      if (timeWindow.should_consider_cut_off_time) {
        const taskCutOffTime = moment(form.date)
          .add(timeWindow.time_from, 'seconds')
          .subtract(timeWindow.cut_off_time, 'minutes');
        const currTime = moment();

        if (taskCutOffTime.isBefore(currTime)) {
          return false;
        }

        return true;
      }
      return true;
    });

    return timeWindowOptions.map(timeWindow => ({
      value: timeWindow.id,
      text: (
        <div>
          <div>{timeWindow.name}</div>
          <div className="vf-job__custom-time__time-window__subtext">
            {`${Utils.Helper.getTimeFromSecond(timeWindow.time_from)} to
              ${Utils.Helper.getTimeFromSecond(timeWindow.time_to)}`}
          </div>
        </div>
      ),
    })).concat(additionalTimeOptions);
  }

  _onChange = () => {
    const { form } = this.state;
    let timeFrom;
    let timeTo;

    // If time_window_id and time_from is empty
    if (Helper.isNullOrUndefined(form.time_window_id) && Helper.isNullOrUndefined(form.time_from)) {
      timeFrom = null;
    } else {
      timeFrom = `${form.date} ${Math.floor(form.time_from / 60) < 10 ? '0' : ''}`
      + `${Math.floor(form.time_from / 60)}:${form.time_from % 60 < 10 ? '0' : ''}`
      + `${form.time_from % 60}`;
    }

    // If time_window_id and time_to is empty
    if (Helper.isNullOrUndefined(form.time_window_id) && Helper.isNullOrUndefined(form.time_to)) {
      timeTo = null;
    } else {
      timeTo = `${form.date} ${Math.floor(form.time_to / 60) < 10 ? '0' : ''}`
      + `${Math.floor(form.time_to / 60)}:${form.time_to % 60 < 10 ? '0' : ''}`
      + `${form.time_to % 60}`;
    }

    // If time_window_id is present
    if (!Helper.isNullOrUndefined(form.time_window_id)) {
      const fTime = this._formatTimeWithTimeWindows(form);
      timeFrom = fTime.timeFrom;
      timeTo = fTime.timeTo;
    }

    this.props.onChange({
      time_type: form.time_type,
      time_window_id: form.time_window_id,
      time_from: timeFrom,
      time_to: timeTo,
    });
  }

  _onFormChange = (fieldName, value) => {
    this.setState((prevState) => {
      const form = { ...prevState.form };
      form[fieldName] = value;

      if (fieldName === 'time_window_id') {
        if (value === -1) {
          form.time_window_id = null;
          form.time_type = 'custom';
          form.time_from = null;
          form.time_to = null;
        } else {
          form.time_window_id = value;
          form.time_type = null;
          form.time_from = null;
          form.time_to = null;
        }
      } else if (fieldName === 'time_from' && Helper.isNullOrUndefined(form.time_to)) {
        form.time_to = value;
      } else if (fieldName === 'time_to' && Helper.isNullOrUndefined(form.time_from)) {
        form.time_from = value;
      }
      return {
        form,
      };
    }, this._onChange);
  }

  _formatTimeWithTimeWindows = (form) => {
    const timeWindow = this.props.timeWindows.find(tw => tw.id === form.time_window_id);
    if (!Helper.isNullOrUndefined(timeWindow)) {
      const timeFrom = moment(form.date).seconds(timeWindow.time_from).format('YYYY-MM-DD HH:mm');
      const timeTo = moment(form.date).seconds(timeWindow.time_to).format('YYYY-MM-DD HH:mm');
      return { timeFrom, timeTo };
    }

    return {
      timeFrom: form.date,
      timeTo: form.date,
    };
  }

  render() {
    const { error, id } = this.props;
    const { form } = this.state;

    return (
      <Components.Form.InputGroup className="vf-job__custom-time">
        <Components.Form.Block
          contentClassName="vf-job__custom-time__date"
          fontWeight="REGULAR"
          iconClassName="versa-calendar-day"
          isInputIcon
          isRequired
          level={0}
        >
          <Components.Input.DatePicker
            className="vf-job__custom-time__date__picker"
            disabled={this.state.isLoadingTimeWindows}
            id={Helper.isNullOrUndefined(id) ? undefined
              : `${id}__date-input`}
            onChange={(value) => {
              if (value !== form.date) {
                this.props.dispatch(ReduxActions.account.fetchTimeWindowsAvailability(value));
              }
              this._onFormChange('date', value);
            }}
            placeholder="Date"
            value={form.date}
          />
        </Components.Form.Block>

        <Components.Form.Divider />

        <Components.Form.Block
          contentClassName="vf-job__custom-time__time-window"
          iconClassName="versa-time"
          isInputIcon
          isRequired
          level={0}
        >
          <Components.Input.UntypableSelect
            error={error.time_window || error.time_type}
            id={Helper.isNullOrUndefined(id) ? undefined
              : `${id}__time-type-select-button`}
            onChange={value => this._onFormChange('time_window_id', value)}
            options={this._getTimeWindowOptions()}
            placeholder="Select Time Window"
            value={form.time_type === 'custom' ? -1 : form.time_window_id}
          />
        </Components.Form.Block>

        {this.state.form.time_type === 'custom'
          ? (
            <Components.Form.Block contentClassName="vf-job__custom-time__time" fontWeight="REGULAR" iconClassName="" isInputIcon level={0}>
              <Components.Input.TimePicker
                error={error.time_from}
                id={Helper.isNullOrUndefined(id) ? undefined
                  : `${id}__time-from-input`}
                onChange={value => this._onFormChange('time_from', value)}
                placeholder="Start Time"
                value={form.time_from}
              />
              <div className="vf-job__custom-time__time__dash">
                -
              </div>
              <Components.Input.TimePicker
                error={error.time_to}
                id={Helper.isNullOrUndefined(id) ? undefined
                  : `${id}__time-to-input`}
                onChange={value => this._onFormChange('time_to', value)}
                placeholder="End Time"
                value={form.time_to}
              />
            </Components.Form.Block>
          ) : false}
      </Components.Form.InputGroup>
    );
  }
}

JobCustomDateTime.displayName = 'Job Custom Date Time';

JobCustomDateTime.propTypes = {
  allowCustomTimeWindows: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.shape({
    time_window: PropTypes.string,
    time_type: PropTypes.string,
    time_from: PropTypes.string,
    time_to: PropTypes.string,
  }),
  id: PropTypes.string,
  onChange: PropTypes.func,
  shouldFilterTimeWindows: PropTypes.bool,
  timeWindows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    time_from: PropTypes.number,
    time_to: PropTypes.number,
  })),
  timeWindowsAvailability: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    time_window_id: PropTypes.number,
    availability: PropTypes.number,
  }))),
  value: PropTypes.shape({}),
};

JobCustomDateTime.defaultProps = {
  allowCustomTimeWindows: false,
  error: {},
  id: null,
  onChange: () => {},
  shouldFilterTimeWindows: true,
  timeWindows: [],
  timeWindowsAvailability: {},
  value: null,
};

function mapStateToProps(state) {
  const { account, profile } = state;
  return {
    allowCustomTimeWindows: profile.profile?.myjobs_setting?.allow_custom_time_windows,
    timeWindows: account.timeWindows || [],
    timeWindowsAvailability: account.timeWindowsAvailability || {},
  };
}

export default connect(mapStateToProps)(JobCustomDateTime);
